import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { downloadReport } from "@/lib/api";
import { inr, fmtDate, today } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/components/PageTitle";
import { DatePicker } from "@/components/DatePicker";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Receipt, FileDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function Expenses() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const [date, setDate] = useState(today());
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [memo, setMemo] = useState("");
  const [editId, setEditId] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses", from, to],
    queryFn: async () =>
      (await api.get("/expenses", { params: { date_from: from || undefined, date_to: to || undefined } })).data,
  });

  const add = useMutation({
    mutationFn: async () => {
      const body = { date, name, amount: Number(amount || 0), memo };
      return editId ? api.put(`/expenses/${editId}`, body) : api.post("/expenses", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      toast.success(editId ? "Expense updated" : "Expense added");
      setName(""); setAmount(""); setMemo(""); setEditId(null);
    },
  });

  const loadEdit = (e) => { setEditId(e.id); setDate(e.date); setName(e.name); setAmount(e.amount); setMemo(e.memo || ""); };
  const cancelEdit = () => { setEditId(null); setName(""); setAmount(""); setMemo(""); };

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/expenses/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["expenses"] }); toast.success("Deleted"); },
  });

  const total = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div>
      <PageTitle title="Expenses" subtitle="Day-to-day store expenses">
        <Button variant="outline" onClick={() => downloadReport("/reports/expenses/export", { fmt: "pdf", date_from: from, date_to: to }, "expenses")}>
          <FileDown className="h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" onClick={() => downloadReport("/reports/expenses/export", { fmt: "excel", date_from: from, date_to: to }, "expenses")}>
          <FileSpreadsheet className="h-4 w-4" /> Excel
        </Button>
      </PageTitle>

      <div className="grid lg:grid-cols-3 gap-4">
        {isAdmin && (
        <Card className="p-5 lg:col-span-1 h-fit">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /> {editId ? "Edit Expense" : "New Expense"}</h3>
          <div className="space-y-3">
            <div><Label>Date</Label><div className="mt-1.5"><DatePicker value={date} onChange={setDate} testid="expense-date" /></div></div>
            <div><Label>Expense Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="e.g. Diesel, Loading charges" data-testid="expense-name" /></div>
            <div><Label>Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid="expense-amount" /></div>
            <div><Label>Memo</Label><Input value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1.5" data-testid="expense-memo" /></div>
            <Button className="w-full" onClick={() => add.mutate()} disabled={!name || !amount || add.isPending} data-testid="add-expense-button">
              <Plus className="h-4 w-4" /> {editId ? "Update Expense" : "Add Expense"}
            </Button>
            {editId && <Button variant="outline" className="w-full" onClick={cancelEdit} data-testid="cancel-expense-edit">Cancel</Button>}
          </div>
        </Card>
        )}

        <Card className="lg:col-span-2 overflow-hidden">
          <div className="p-4 flex flex-col sm:flex-row gap-3 border-b border-border items-end">
            <div className="flex-1"><Label className="text-xs">From</Label><div className="mt-1"><DatePicker value={from} onChange={setFrom} testid="expense-from" /></div></div>
            <div className="flex-1"><Label className="text-xs">To</Label><div className="mt-1"><DatePicker value={to} onChange={setTo} testid="expense-to" /></div></div>
            <div className="rounded-lg bg-primary/10 px-4 py-2 text-primary">
              <div className="text-xs">Total</div><div className="font-bold data-num">{inr(total)}</div>
            </div>
          </div>
          <div className="overflow-x-auto ssacc-scroll">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Memo</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No expenses.</TableCell></TableRow>
                ) : expenses.map((e) => (
                  <TableRow key={e.id} data-testid={`expense-row`}>
                    <TableCell>{fmtDate(e.date)}</TableCell>
                    <TableCell className="font-medium">{e.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.memo || "—"}</TableCell>
                    <TableCell className="text-right font-semibold data-num">{inr(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                      <div className="flex justify-end">
                      <Button size="icon" variant="ghost" onClick={() => loadEdit(e)} data-testid="edit-expense"><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="text-destructive"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete expense?</AlertDialogTitle>
                          <AlertDialogDescription>{e.name} — {inr(e.amount)}</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => del.mutate(e.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
